import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Slider from "react-slick";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import { toast } from "react-toastify";

import {
  fetchAllClients,
  followClient,
} from "../redux/slices/clientFollowSlice";

// =====================================================
// FALLBACK PROFILE IMAGE
// =====================================================
const FALLBACK_IMAGE =
  "/assets/images/user.png";

// =====================================================
// LOGGED-IN USER TYPE
// =====================================================
interface LoggedUser {
  user_id?: number | string;
}

// =====================================================
// SUGGESTED CLIENT TYPE
//
// Response field names match the backend API.
// logo_image now contains the complete public URL.
// =====================================================
interface SuggestedClient {
  client_id: number;
  refer_id?: number | null;

  level_id?: number | null;
  level_name?: string | null;

  client_name?: string | null;
  unique_slug?: string | null;

  client_email?: string | null;
  client_domain?: string | null;

  banner_image?: string | null;
  logo_image?: string | null;

  client_status?: number | string;
  verified_status?: number | string;

  follower_count?: number | string;
  total_followers?: number | string;

  member_count?: number | string;
  total_members?: number | string;

  is_following?: number | string;
}

// =====================================================
// FRIEND / CLUB SUGGESTION SLIDER
// =====================================================
const FriendSlider: React.FC = () => {
  const dispatch = useDispatch<any>();

  const {
    clients = [],
    loading = false,
  } = useSelector(
    (state: any) =>
      state.clientFollow || {}
  );

  const [
    followingClientId,
    setFollowingClientId,
  ] = useState<number | null>(null);

  // ===================================================
  // READ USER FROM LOCAL STORAGE
  // ===================================================
  const user = useMemo<LoggedUser>(
    () => {
      try {
        const storedUser =
          localStorage.getItem("user");

        if (!storedUser) {
          return {};
        }

        return JSON.parse(storedUser);
      } catch (error) {
        console.error(
          "[FriendSlider] Invalid localStorage user data",
          error
        );

        return {};
      }
    },
    []
  );

  const userId = Number(
    user.user_id || 0
  );

  // ===================================================
  // SLIDER SETTINGS
  // ===================================================
  const settings = useMemo(
    () => ({
      arrows: false,
      dots: false,
      infinite: false,
      speed: 300,
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: false,
      variableWidth: true,
      swipeToSlide: true,
      accessibility: true,
    }),
    []
  );

  // ===================================================
  // FETCH SUGGESTED CLUBS
  //
  // Backend excludes clubs already followed by user.
  // ===================================================
  const loadClients =
    useCallback(async () => {
      if (!userId) {
        return;
      }

      try {
        await dispatch(
          fetchAllClients({
            user_id: userId,
          })
        );
      } catch (error) {
        console.error(
          "[FriendSlider] Failed to fetch suggested clubs",
          error
        );
      }
    }, [dispatch, userId]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // ===================================================
  // FOLLOW CLIENT
  // ===================================================
  const handleFollow =
    useCallback(
      async (
        client: SuggestedClient
      ) => {
        if (!userId) {
          toast.error(
            "Please login first"
          );

          return;
        }

        const clientId = Number(
          client.client_id
        );

        if (
          !Number.isInteger(clientId) ||
          clientId <= 0
        ) {
          toast.error(
            "Invalid club selected"
          );

          return;
        }

        if (
          followingClientId !== null
        ) {
          return;
        }

        setFollowingClientId(
          clientId
        );

        try {
          const result =
            await dispatch(
              followClient({
                client_id: clientId,
                follower_user_id:
                  userId,
                platform: "web",
              })
            );

          if (
            followClient.fulfilled.match(
              result
            )
          ) {
            const payload =
              result.payload || {};

            const isSuccess =
              payload.success === true ||
              payload.status === true;

            if (isSuccess) {
              toast.success(
                payload.message ||
                  "Club followed successfully"
              );

              await loadClients();
              return;
            }

            toast.error(
              payload.message ||
                "Follow failed"
            );

            return;
          }

          toast.error(
            result.payload?.message ||
              result.error?.message ||
              "Follow failed"
          );
        } catch (error) {
          console.error(
            "[FriendSlider] Follow client error",
            error
          );

          toast.error(
            "Unable to follow this club"
          );
        } finally {
          setFollowingClientId(
            null
          );
        }
      },
      [
        dispatch,
        followingClientId,
        loadClients,
        userId,
      ]
    );

  // ===================================================
  // FRONTEND SAFETY FILTER
  //
  // Backend already filters inactive and followed clubs.
  // ===================================================
  const filteredClients =
    useMemo<SuggestedClient[]>(
      () => {
        if (
          !Array.isArray(clients)
        ) {
          return [];
        }

        return clients.filter(
          (
            client: SuggestedClient
          ) =>
            Number(
              client.client_status
            ) === 1 &&
            Number(
              client.is_following
            ) !== 1
        );
      },
      [clients]
    );

  // ===================================================
  // LOADING STATE
  // ===================================================
  if (loading) {
    return (
      <div
        className="p-3 text-center text-grey-500"
        role="status"
      >
        Loading clubs...
      </div>
    );
  }

  // ===================================================
  // EMPTY STATE
  // ===================================================
  if (
    filteredClients.length === 0
  ) {
    return null;
  }

  return (
    <Slider {...settings}>
      {filteredClients.map(
        (client) => {
          const clientId =
            Number(
              client.client_id
            );

          const isFollowing =
            followingClientId ===
            clientId;

          return (
            <div
              key={clientId}
              className="card w150 d-block border-0 shadow-xss rounded-3 overflow-hidden mb-3 me-3"
            >
              <div className="card-body d-block w-100 ps-3 pe-3 pb-4 text-center">
                <figure className="overflow-hidden avatar ms-auto me-auto mb-0 position-relative w65 z-index-1">
                  <img
                    src={
                      client.logo_image ||
                      FALLBACK_IMAGE
                    }
                    alt={
                      client.client_name ||
                      "Club"
                    }
                    className="float-right p-0 bg-white rounded-circle w-100 shadow-xss"
                    loading="lazy"
                    onError={(
                      event: React.SyntheticEvent<HTMLImageElement>
                    ) => {
                      const image =
                        event.currentTarget;

                      image.onerror =
                        null;

                      image.src =
                        FALLBACK_IMAGE;
                    }}
                  />
                </figure>

                <h4 className="fw-700 font-xssss mt-3 mb-1 d-block w-100 text-truncate">
                  {client.client_name ||
                    "Club"}
                </h4>

                <p className="fw-500 font-xsssss text-grey-500 mt-0 mb-3 lh-2 text-truncate">
                  {client.level_name ||
                    "Club"}
                </p>

                <button
                  type="button"
                  onClick={() =>
                    handleFollow(
                      client
                    )
                  }
                  disabled={
                    followingClientId !==
                    null
                  }
                  className="text-center p-2 lh-20 w100 ms-1 ls-3 d-inline-block rounded-xl bg-success font-xsssss fw-700 text-white border-0"
                  style={{
                    cursor:
                      isFollowing
                        ? "not-allowed"
                        : "pointer",

                    opacity:
                      isFollowing
                        ? 0.7
                        : 1,
                  }}
                >
                  {isFollowing
                    ? "FOLLOWING..."
                    : "FOLLOW"}
                </button>
              </div>
            </div>
          );
        }
      )}
    </Slider>
  );
};

export default FriendSlider;